<script setup lang="ts">
import { computed } from 'vue'
import { ORDER_STATUS } from '@/constants/orderStatus'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import SendAdvRequirementsInfo from './SendAdvRequirementsInfo.vue'

const props = defineProps<{
  status: string
  isAuthorOrCommercial: boolean
  canUnblock?: boolean
  canSendToAdv: boolean
  hideSendAdv?: boolean
  sendAdvBlockReason: string | null
  sendAdvRequirements: { signed: boolean; typeContrat: boolean; modePaiement: boolean; famille: boolean; devisSigne?: boolean, hasComment?: boolean }
  isSaving: boolean
  isUpdatingStatus: boolean
  isEditMode?: boolean
  canValidateBuManager?: boolean
  needsBuManagerValidation?: boolean
}>()

const emit = defineEmits<{
  (e: 'save', backToDraft?: boolean): void
  (e: 'send-adv'): void
  (e: 'cancel'): void
  (e: 'delete'): void
  (e: 'resend-adv'): void
  (e: 'unblock'): void
  (e: 'back-to-draft'): void
  (e: 'info-hover', value: boolean): void
  (e: 'validate-bu-manager'): void
  (e: 'refuse-bu-manager'): void
}>()

const isDraft = computed(() => {
  const s = String(props.status || '').toUpperCase()
  return s === ORDER_STATUS.DRAFT || s === 'BROUILLON'
})
const isWaiting = computed(() => String(props.status || '').toUpperCase() === ORDER_STATUS.WAITING)
const isInProgress = computed(() => String(props.status || '').toUpperCase() === ORDER_STATUS.IN_PROGRESS)
const isBlockedBusiness = computed(() => String(props.status || '').toUpperCase() === ORDER_STATUS.BLOCKED_BUSINESS)
const isBlockedOperator = computed(() => String(props.status || '').toUpperCase() === ORDER_STATUS.BLOCKED_OPERATOR)
const isCanceled = computed(() => String(props.status || '').toUpperCase() === ORDER_STATUS.CANCELED)
const isSentToBuManager = computed(() => String(props.status || '').toUpperCase() === ORDER_STATUS.SENT_TO_BU_MANAGER)

const canShowSave = computed(() => {
  return isDraft.value || isWaiting.value || isInProgress.value || isBlockedBusiness.value || isBlockedOperator.value || isSentToBuManager.value
})

const canShowSendAdv = computed(() => isDraft.value && !props.hideSendAdv)

const canShowCancel = computed(() => props.isEditMode && (isDraft.value || isWaiting.value || isSentToBuManager.value))

const canShowDelete = computed(() => props.isEditMode && (isDraft.value || isCanceled.value))

const canShowBackToDraft = computed(() => isWaiting.value && props.isAuthorOrCommercial)

const canShowResendAdv = computed(() => isBlockedBusiness.value && props.isAuthorOrCommercial && !props.hideSendAdv)

const canShowUnblock = computed(() => isBlockedBusiness.value && props.canUnblock)

const req = computed(() => props.sendAdvRequirements ?? { signed: false, typeContrat: false, modePaiement: false, famille: false, devisSigne: false, hasComment: false})
const canSendToManager = computed(() => req.value.signed === true)
</script>

<template>
  <div class="flex gap-3 shrink-0">
    <ButtonDaisy
      v-if="canShowBackToDraft"
      text="Passer en brouillon"
      variant="secondary"
      size="sm"
      :loading="isSaving"
      @click="emit('save', true)"
    />

    <ButtonDaisy
      v-if="canShowSave"
      text="Enregistrer"
      variant="secondary"
      size="sm"
      :loading="isSaving"
      @click="emit('save')"
    />

    <ButtonDaisy
      v-if="canShowCancel"
      text="Annuler"
      variant="error"
      size="sm"
      :loading="isUpdatingStatus"
      @click="emit('cancel')"
    />

    <ButtonDaisy
      v-if="canShowDelete"
      :text="isCanceled ? 'Supprimer la commande' : 'Supprimer le brouillon'"
      variant="error"
      size="sm"
      :loading="isUpdatingStatus"
      @click="emit('delete')"
    />

    <ButtonDaisy
      v-if="canShowUnblock"
      text="Débloquer"
      variant="primary"
      size="sm"
      :loading="isUpdatingStatus"
      @click="emit('unblock')"
    />

    <div v-if="canShowResendAdv" class="flex items-center gap-1">
      <SendAdvRequirementsInfo :requirements="req" @info-hover="emit('info-hover', $event)" />
      <div :title="sendAdvBlockReason ?? undefined">
        <ButtonDaisy
          text="Renvoyer ADV"
          variant="primary"
          size="sm"
          :disabled="!canSendToAdv"
          :loading="isUpdatingStatus"
          @click="emit('resend-adv')"
        />
      </div>
    </div>

    <div v-if="canShowSendAdv" class="flex items-center gap-1">
      <SendAdvRequirementsInfo v-if="!needsBuManagerValidation" :requirements="req" @info-hover="emit('info-hover', $event)" />
      <div :title="needsBuManagerValidation ? undefined : (sendAdvBlockReason ?? undefined)">
        <ButtonDaisy
          :text="needsBuManagerValidation ? 'Envoyer à mon Manager' : 'Envoyer ADV'"
          variant="primary"
          size="sm"
          :disabled="needsBuManagerValidation ? !canSendToManager : !canSendToAdv"
          :loading="isSaving"
          @click="emit('send-adv')"
        />
      </div>
    </div>

    <ButtonDaisy
      v-if="isSentToBuManager && canValidateBuManager"
      text="Refuser et renvoyer à l'IC"
      variant="error"
      size="sm"
      :loading="isUpdatingStatus"
      @click="emit('refuse-bu-manager')"
    />

    <ButtonDaisy
      v-if="isSentToBuManager && canValidateBuManager"
      text="Valider Manager et Envoyer ADV"
      variant="primary"
      size="sm"
      :loading="isUpdatingStatus"
      @click="emit('validate-bu-manager')"
    />
  </div>
</template>
